import { useEffect, useMemo, useState, useCallback } from "react";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileUpload, fileToUrl } from "@/components/FileUpload";
import CloudflareVideoUpload from "@/components/CloudflareVideoUpload";
import {
  Plus, Pencil, Trash2, Video, Paperclip, X, ChevronDown, ChevronLeft,
  GripVertical, FolderPlus, FileText, Palette, ExternalLink, Type, PlayCircle, ArrowUp, ArrowDown, Copy, ImageIcon,
} from "lucide-react";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  extractVimeoId, vimeoEmbedUrl, newBlockId, newSectionId, THEMES, getLessonBlocks,
} from "@/lib/courseUtils";

const emptyCourse = { title: "", subtitle: "", description: "", cover_image: "", instructor_name: "", duration_label: "", status: "published", theme: "apple" };

function backendUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
}

export default function AdminCourses() {
  const [courses, setCourses] = useState([]);
  const [showCourse, setShowCourse] = useState(false);
  const [editing, setEditing] = useState(emptyCourse);
  const [expanded, setExpanded] = useState(null);
  const [courseData, setCourseData] = useState({}); // {courseId: {course, lessons}}
  const [lessonEditor, setLessonEditor] = useState({ open: false, courseId: null, sectionId: null, subsectionId: null, lesson: null });

  const loadCourses = useCallback(() => api.get("/courses/admin/list").then((r) => setCourses(r.data)), []);
  const loadCourseFull = useCallback(async (courseId) => {
    const { data } = await api.get(`/courses/${courseId}`);
    setCourseData((s) => ({ ...s, [courseId]: data }));
  }, []);

  useEffect(() => { loadCourses(); }, [loadCourses]);

  const saveCourse = async (e) => {
    e.preventDefault();
    try {
      if (editing.id) await api.patch(`/courses/admin/${editing.id}`, editing);
      else await api.post("/courses/admin", editing);
      toast.success("تم حفظ الدورة");
      setShowCourse(false); setEditing(emptyCourse); loadCourses();
    } catch (err) { toast.error(extractError(err)); }
  };

  const delCourse = async (id) => {
    if (!window.confirm("حذف الدورة نهائيًا مع كل دروسها؟")) return;
    await api.delete(`/courses/admin/${id}`); toast.success("تم"); loadCourses();
  };

  const setTheme = async (courseId, theme) => {
    try {
      await api.post(`/courses/admin/${courseId}/theme`, { theme });
      toast.success("تم تحديث الستايل");
      loadCourses(); loadCourseFull(courseId);
    } catch (e) { toast.error(extractError(e)); }
  };

  const toggle = (id) => {
    if (expanded === id) setExpanded(null);
    else { setExpanded(id); loadCourseFull(id); }
  };

  const persistReorder = useCallback(async (courseId, sections, lessons) => {
    try {
      await api.patch(`/courses/admin/${courseId}/reorder`, { sections, lessons });
    } catch (e) { toast.error("فشل حفظ الترتيب"); throw e; }
  }, []);

  // Top-level course drag-and-drop
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const onCourseDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = courses.findIndex((c) => c.id === active.id);
    const newIdx = courses.findIndex((c) => c.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(courses, oldIdx, newIdx);
    setCourses(next);                         // optimistic update
    try {
      await api.patch("/courses/admin/reorder", { ids: next.map((c) => c.id) });
    } catch (e) {
      toast.error("فشل حفظ ترتيب الدورات");
      loadCourses();                          // rollback
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-courses">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-semibold">إدارة الدورات</h1>
          <p className="text-sm text-foreground/60 mt-1">أنشئ دورات ذات أقسام قابلة للترتيب بالسحب، مع دروس متعددة الفيديوهات والنصوص.</p>
        </div>
        <Dialog open={showCourse} onOpenChange={(v) => { setShowCourse(v); if (!v) setEditing(emptyCourse); }}>
          <DialogTrigger asChild>
            <Button className="gap-2 rounded-full bg-black hover:bg-black/80 text-white" data-testid="btn-new-course"><Plus className="h-4 w-4" /> دورة جديدة</Button>
          </DialogTrigger>
          <DialogContent dir="rtl" className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing.id ? "تعديل الدورة" : "دورة جديدة"}</DialogTitle></DialogHeader>
            <form onSubmit={saveCourse} className="space-y-3">
              <div className="space-y-2"><Label>العنوان</Label><Input required value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="course-title-input" /></div>
              <div className="space-y-2"><Label>العنوان الفرعي</Label><Input value={editing.subtitle || ""} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} /></div>
              <div className="space-y-2"><Label>الوصف</Label><Textarea value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>اسم المدرب</Label><Input value={editing.instructor_name || ""} onChange={(e) => setEditing({ ...editing, instructor_name: e.target.value })} /></div>
                <div className="space-y-2"><Label>المدة (نص)</Label><Input value={editing.duration_label || ""} onChange={(e) => setEditing({ ...editing, duration_label: e.target.value })} /></div>
              </div>
              <div className="space-y-2"><Label>صورة الغلاف</Label>
                <FileUpload kind="image" value={editing.cover_image} onChange={(v) => setEditing({ ...editing, cover_image: fileToUrl(v) })} testid="course-cover-upload" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Palette className="h-4 w-4" /> ستايل الدورة (يمكن تغييره لاحقًا)</Label>
                <Select value={editing.theme || "apple"} onValueChange={(v) => setEditing({ ...editing, theme: v })}>
                  <SelectTrigger data-testid="course-theme-input"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {THEMES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full rounded-full bg-black hover:bg-black/80 text-white" data-testid="course-save">حفظ الدورة</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={onCourseDragEnd}>
        <SortableContext items={courses.map((c) => c.id)} strategy={verticalListSortingStrategy}>
          {courses.map((c) => (
            <SortableCourseCard key={c.id} c={c}>
              <div className="flex items-center gap-4 flex-wrap">
                {c.cover_image && <img src={backendUrl(c.cover_image)} className="h-16 w-24 object-cover rounded-lg" alt="" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display font-semibold text-lg">{c.title}</h3>
                    <span className="text-[10px] uppercase tracking-widest bg-orange-100 text-orange-800 rounded-full px-2 py-0.5">{c.theme || "apple"}</span>
                  </div>
                  <p className="text-xs opacity-70 mt-1">{c.lessons_count} درس • {c.enrolled_count} مسجّل • {c.status}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Select value={c.theme || "apple"} onValueChange={(v) => setTheme(c.id, v)}>
                    <SelectTrigger className="h-8 text-xs w-40" data-testid={`course-theme-select-${c.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {THEMES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" onClick={() => toggle(c.id)} className="gap-1" data-testid={`btn-expand-course-${c.id}`}>
                    {expanded === c.id ? <ChevronDown className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3 rtl:-scale-x-100" />}
                    البناء
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(c); setShowCourse(true); }} data-testid={`btn-edit-course-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => delCourse(c.id)} className="text-destructive" data-testid={`btn-del-course-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>

              {expanded === c.id && courseData[c.id] && (
                <CourseBuilder
                  courseId={c.id}
                  courseData={courseData[c.id]}
                  reload={() => loadCourseFull(c.id)}
                  persistReorder={persistReorder}
                  onLessonEdit={(sectionId, subsectionId, lesson) => setLessonEditor({ open: true, courseId: c.id, sectionId, subsectionId, lesson })}
                />
              )}
            </SortableCourseCard>
          ))}
        </SortableContext>
      </DndContext>

      {lessonEditor.open && (
        <LessonEditor
          state={lessonEditor}
          onClose={() => setLessonEditor({ open: false, courseId: null, sectionId: null, subsectionId: null, lesson: null })}
          onSaved={() => { loadCourseFull(lessonEditor.courseId); loadCourses(); }}
        />
      )}
    </div>
  );
}

// ===========================================================================
// Top-level course card — sortable via a left-side drag handle
// ===========================================================================
function SortableCourseCard({ c, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: c.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="relative">
      <Card className={`p-4 bg-white border-foreground/10 shadow-sm ${isDragging ? "ring-2 ring-orange-400" : ""}`} data-testid={`admin-course-${c.id}`}>
        <div className="flex items-stretch gap-3">
          <button
            type="button"
            className="cursor-grab active:cursor-grabbing text-foreground/30 hover:text-orange-500 transition-colors self-center px-1 -mx-1"
            title="اسحب لإعادة الترتيب"
            data-testid={`course-drag-${c.id}`}
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-5 w-5" />
          </button>
          <div className="flex-1 min-w-0">{children}</div>
        </div>
      </Card>
    </div>
  );
}

// ===========================================================================
// Course Builder — drag-drop sections/subsections/lessons
// ===========================================================================
function CourseBuilder({ courseId, courseData, reload, persistReorder, onLessonEdit }) {
  const course = courseData.course;
  const allLessons = courseData.lessons || [];

  const [sections, setSections] = useState(() => Array.isArray(course.sections) ? course.sections : []);
  useEffect(() => { setSections(Array.isArray(course.sections) ? course.sections : []); }, [course.sections]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const lessonsBySection = useMemo(() => {
    const m = {};
    for (const l of allLessons) {
      const key = `${l.section_id || "__none__"}::${l.subsection_id || "__none__"}`;
      m[key] = m[key] || [];
      m[key].push(l);
    }
    for (const k of Object.keys(m)) m[k].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return m;
  }, [allLessons]);

  const addSection = async () => {
    const title = window.prompt("عنوان القسم:");
    if (!title) return;
    const next = [...sections, { id: newSectionId(), title, order: sections.length, subsections: [] }];
    setSections(next);
    await persistReorder(courseId, next, null);
    reload();
  };

  const addSubsection = async (secId) => {
    const title = window.prompt("عنوان القسم الفرعي:");
    if (!title) return;
    const next = sections.map((s) => s.id === secId
      ? { ...s, subsections: [...(s.subsections || []), { id: newSectionId(), title, order: (s.subsections || []).length }] }
      : s);
    setSections(next);
    await persistReorder(courseId, next, null);
    reload();
  };

  const renameSection = async (secId) => {
    const cur = sections.find((s) => s.id === secId);
    const t = window.prompt("عنوان القسم:", cur?.title || "");
    if (t == null) return;
    const next = sections.map((s) => s.id === secId ? { ...s, title: t } : s);
    setSections(next);
    await persistReorder(courseId, next, null);
    reload();
  };

  const renameSubsection = async (secId, subId) => {
    const cur = sections.find((s) => s.id === secId)?.subsections?.find((x) => x.id === subId);
    const t = window.prompt("عنوان القسم الفرعي:", cur?.title || "");
    if (t == null) return;
    const next = sections.map((s) => s.id === secId
      ? { ...s, subsections: s.subsections.map((x) => x.id === subId ? { ...x, title: t } : x) }
      : s);
    setSections(next);
    await persistReorder(courseId, next, null);
    reload();
  };

  const deleteSection = async (secId) => {
    if (!window.confirm("حذف القسم؟ الدروس ستبقى لكن ستنتقل إلى 'بدون قسم'.")) return;
    const next = sections.filter((s) => s.id !== secId);
    setSections(next);
    // Move lessons out of this section
    const patch = allLessons.filter((l) => l.section_id === secId).map((l) => ({ id: l.id, section_id: null, subsection_id: null, order: l.order }));
    await persistReorder(courseId, next, patch);
    reload();
  };

  const dupSection = async (secId) => {
    try {
      const { data } = await api.post(`/courses/admin/${courseId}/sections/${secId}/duplicate`);
      toast.success(`تم نسخ القسم مع ${data.lessons_copied} درس`);
      reload();
    } catch (e) { toast.error(extractError(e)); }
  };

  const deleteSubsection = async (secId, subId) => {
    if (!window.confirm("حذف القسم الفرعي؟ الدروس ستبقى في القسم الأب.")) return;
    const next = sections.map((s) => s.id === secId
      ? { ...s, subsections: s.subsections.filter((x) => x.id !== subId) }
      : s);
    setSections(next);
    const patch = allLessons.filter((l) => l.subsection_id === subId).map((l) => ({ id: l.id, section_id: secId, subsection_id: null, order: l.order }));
    await persistReorder(courseId, next, patch);
    reload();
  };

  const onSectionDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = sections.findIndex((s) => s.id === active.id);
    const newIdx = sections.findIndex((s) => s.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(sections, oldIdx, newIdx).map((s, i) => ({ ...s, order: i }));
    setSections(next);
    await persistReorder(courseId, next, null);
  };

  const onLessonDragEnd = async (sectionId, subsectionId, evt) => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    const key = `${sectionId || "__none__"}::${subsectionId || "__none__"}`;
    const list = [...(lessonsBySection[key] || [])];
    const oldIdx = list.findIndex((l) => l.id === active.id);
    const newIdx = list.findIndex((l) => l.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const moved = arrayMove(list, oldIdx, newIdx).map((l, i) => ({
      id: l.id, section_id: sectionId, subsection_id: subsectionId, order: i,
    }));
    await persistReorder(courseId, null, moved);
    reload();
  };

  const addLesson = async (sectionId, subsectionId) => {
    const title = window.prompt("عنوان الدرس:");
    if (!title) return;
    const key = `${sectionId || "__none__"}::${subsectionId || "__none__"}`;
    const order = (lessonsBySection[key] || []).length;
    try {
      await api.post(`/courses/admin/${courseId}/lessons`, {
        title, section_id: sectionId, subsection_id: subsectionId, order, blocks: [], duration_min: 5,
      });
      reload();
    } catch (e) { toast.error(extractError(e)); }
  };

  const delLesson = async (lessonId) => {
    if (!window.confirm("حذف الدرس؟")) return;
    await api.delete(`/courses/admin/${courseId}/lessons/${lessonId}`);
    toast.success("تم"); reload();
  };

  const dupLesson = async (lessonId) => {
    try {
      await api.post(`/courses/admin/${courseId}/lessons/${lessonId}/duplicate`);
      toast.success("تم نسخ الدرس");
      reload();
    } catch (e) { toast.error(extractError(e)); }
  };

  const unassignedKey = "__none__::__none__";
  const unassigned = lessonsBySection[unassignedKey] || [];

  return (
    <div className="mt-4 border-t border-foreground/10 pt-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h4 className="font-display font-semibold">أقسام ودروس الدورة</h4>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={addSection} data-testid="btn-add-section" className="gap-1">
            <FolderPlus className="h-3 w-3" /> قسم جديد
          </Button>
          <Button size="sm" variant="outline" onClick={() => addLesson(null, null)} className="gap-1" data-testid="btn-add-lesson-unassigned">
            <Plus className="h-3 w-3" /> درس بدون قسم
          </Button>
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onSectionDragEnd}>
        <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {sections.map((sec) => (
              <SortableSection key={sec.id} sec={sec}
                lessonsBySection={lessonsBySection}
                onAddSubsection={() => addSubsection(sec.id)}
                onAddLesson={(subId) => addLesson(sec.id, subId)}
                onRename={() => renameSection(sec.id)}
                onRenameSubsection={(subId) => renameSubsection(sec.id, subId)}
                onDelete={() => deleteSection(sec.id)}
                onDuplicate={() => dupSection(sec.id)}
                onDeleteSubsection={(subId) => deleteSubsection(sec.id, subId)}
                onLessonDragEnd={onLessonDragEnd}
                sensors={sensors}
                onLessonEdit={(subId, lesson) => onLessonEdit(sec.id, subId, lesson)}
                onLessonDelete={delLesson}
                onLessonDuplicate={dupLesson}
              />
            ))}
            {sections.length === 0 && (
              <div className="text-sm opacity-60 py-4 text-center border border-dashed border-foreground/15 rounded-lg">
                لا يوجد أقسام. اضغط "قسم جديد" لبدء بناء الدورة.
              </div>
            )}
          </div>
        </SortableContext>
      </DndContext>

      {/* Unassigned lessons */}
      {unassigned.length > 0 && (
        <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-amber-900">دروس بدون قسم</div>
            <span className="text-xs text-amber-700">{unassigned.length}</span>
          </div>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => onLessonDragEnd(null, null, e)}>
            <SortableContext items={unassigned.map((l) => l.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-1">
                {unassigned.map((l) => (
                  <SortableLesson key={l.id} l={l} onEdit={() => onLessonEdit(null, null, l)} onDelete={() => delLesson(l.id)} onDuplicate={() => dupLesson(l.id)} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Sortable Section
// ===========================================================================
function SortableSection({ sec, lessonsBySection, onAddSubsection, onAddLesson, onRename, onRenameSubsection, onDelete, onDuplicate, onDeleteSubsection, onLessonDragEnd, sensors, onLessonEdit, onLessonDelete, onLessonDuplicate }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sec.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const directLessons = lessonsBySection[`${sec.id}::__none__`] || [];

  return (
    <div ref={setNodeRef} style={style} className="bg-black/[0.02] rounded-lg border border-foreground/10" data-testid={`section-${sec.id}`}>
      <div className="flex items-center gap-2 p-3">
        <button className="cursor-grab active:cursor-grabbing text-foreground/40 hover:text-foreground" {...attributes} {...listeners} data-testid={`section-drag-${sec.id}`}>
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="flex-1 font-semibold text-sm">{sec.title || "قسم بدون عنوان"}</div>
        <span className="text-[10px] text-foreground/50">{directLessons.length + (sec.subsections || []).reduce((n, sub) => n + (lessonsBySection[`${sec.id}::${sub.id}`]?.length || 0), 0)} درس</span>
        <Button size="sm" variant="ghost" onClick={() => onAddLesson(null)} title="إضافة درس مباشر" data-testid={`section-add-lesson-${sec.id}`}><Plus className="h-3 w-3" /></Button>
        <Button size="sm" variant="ghost" onClick={onAddSubsection} title="إضافة قسم فرعي" data-testid={`section-add-sub-${sec.id}`}><FolderPlus className="h-3 w-3" /></Button>
        <Button size="sm" variant="ghost" onClick={onRename} title="إعادة تسمية" data-testid={`section-rename-${sec.id}`}><Pencil className="h-3 w-3" /></Button>
        <Button size="sm" variant="ghost" onClick={onDuplicate} title="نسخ القسم مع كل الدروس" data-testid={`section-dup-${sec.id}`}><Copy className="h-3 w-3" /></Button>
        <Button size="sm" variant="ghost" onClick={onDelete} className="text-destructive" title="حذف" data-testid={`section-delete-${sec.id}`}><Trash2 className="h-3 w-3" /></Button>
      </div>

      <div className="px-3 pb-3 space-y-2">
        {/* Direct lessons under this section (no subsection) */}
        {directLessons.length > 0 && (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => onLessonDragEnd(sec.id, null, e)}>
            <SortableContext items={directLessons.map((l) => l.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-1">
                {directLessons.map((l) => (
                  <SortableLesson key={l.id} l={l} onEdit={() => onLessonEdit(null, l)} onDelete={() => onLessonDelete(l.id)} onDuplicate={() => onLessonDuplicate(l.id)} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {/* Subsections */}
        {(sec.subsections || []).map((sub) => {
          const lessons = lessonsBySection[`${sec.id}::${sub.id}`] || [];
          return (
            <div key={sub.id} className="ps-4 border-s-2 border-orange-200 space-y-1" data-testid={`subsection-${sub.id}`}>
              <div className="flex items-center gap-2 pt-1">
                <FolderPlus className="h-3 w-3 text-orange-500" />
                <div className="flex-1 text-sm font-medium text-orange-900">{sub.title}</div>
                <span className="text-[10px] text-foreground/50">{lessons.length}</span>
                <Button size="icon" variant="ghost" onClick={() => onAddLesson(sub.id)} className="h-6 w-6" data-testid={`sub-add-lesson-${sub.id}`}><Plus className="h-3 w-3" /></Button>
                <Button size="icon" variant="ghost" onClick={() => onRenameSubsection(sub.id)} className="h-6 w-6" data-testid={`sub-rename-${sub.id}`}><Pencil className="h-3 w-3" /></Button>
                <Button size="icon" variant="ghost" onClick={() => onDeleteSubsection(sub.id)} className="h-6 w-6 text-destructive" data-testid={`sub-delete-${sub.id}`}><Trash2 className="h-3 w-3" /></Button>
              </div>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => onLessonDragEnd(sec.id, sub.id, e)}>
                <SortableContext items={lessons.map((l) => l.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-1">
                    {lessons.map((l) => (
                      <SortableLesson key={l.id} l={l} onEdit={() => onLessonEdit(sub.id, l)} onDelete={() => onLessonDelete(l.id)} onDuplicate={() => onLessonDuplicate(l.id)} />
                    ))}
                    {lessons.length === 0 && <div className="text-[11px] text-foreground/40 py-1 italic">لا دروس بعد</div>}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SortableLesson({ l, onEdit, onDelete, onDuplicate }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: l.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const blocks = getLessonBlocks(l);
  const videoCount = blocks.filter((b) => b.type === "video").length;
  const textCount = blocks.filter((b) => b.type === "text").length;
  const imageCount = blocks.filter((b) => b.type === "image").length;
  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2 bg-white rounded p-2 border border-foreground/5 hover:border-orange-300 transition-colors" data-testid={`lesson-row-${l.id}`}>
      <button className="cursor-grab active:cursor-grabbing text-foreground/40 hover:text-foreground" {...attributes} {...listeners}>
        <GripVertical className="h-3 w-3" />
      </button>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{l.title}</div>
        <div className="flex items-center gap-2 text-[10px] text-foreground/50 mt-0.5">
          {videoCount > 0 && <span className="flex items-center gap-0.5"><Video className="h-2.5 w-2.5" />{videoCount}</span>}
          {textCount > 0 && <span className="flex items-center gap-0.5"><Type className="h-2.5 w-2.5" />{textCount}</span>}
          {l.duration_min ? <span>{l.duration_min}د</span> : null}
        </div>
      </div>
      {onDuplicate && (
        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onDuplicate} title="نسخ الدرس" data-testid={`btn-dup-lesson-${l.id}`}><Copy className="h-3 w-3" /></Button>
      )}
      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onEdit} data-testid={`btn-edit-lesson-${l.id}`}><Pencil className="h-3 w-3" /></Button>
      <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={onDelete} data-testid={`btn-del-lesson-${l.id}`}><Trash2 className="h-3 w-3" /></Button>
    </div>
  );
}

// ===========================================================================
// Lesson Editor with multi-block content
// ===========================================================================
function LessonEditor({ state, onClose, onSaved }) {
  const { courseId, lesson } = state;
  const [title, setTitle] = useState(lesson?.title || "");
  const [duration, setDuration] = useState(lesson?.duration_min || 5);
  const [isFreePreview, setIsFreePreview] = useState(!!lesson?.is_free_preview);
  const [blocks, setBlocks] = useState(() => {
    const existing = getLessonBlocks(lesson);
    return existing.length > 0 ? existing : [];
  });
  const [attachments, setAttachments] = useState(lesson?.attachments || []);
  const [saving, setSaving] = useState(false);

  const addVideoBlock = () => setBlocks((b) => [...b, {
    id: newBlockId(), type: "video", order: b.length, video_source: "vimeo",
    vimeo_id: "", upload_url: "", video_title: "", caption: "",
  }]);
  const addTextBlock = () => setBlocks((b) => [...b, {
    id: newBlockId(), type: "text", order: b.length, content: "",
  }]);
  const addImageBlock = () => setBlocks((b) => [...b, {
    id: newBlockId(), type: "image", order: b.length, image_url: "", image_alt: "", caption: "",
  }]);
  const removeBlock = (id) => setBlocks((b) => b.filter((x) => x.id !== id));
  const updateBlock = (id, patch) => setBlocks((b) => b.map((x) => x.id === id ? { ...x, ...patch } : x));
  const moveBlock = (id, dir) => setBlocks((b) => {
    const idx = b.findIndex((x) => x.id === id);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= b.length) return b;
    const copy = [...b];
    [copy[idx], copy[j]] = [copy[j], copy[idx]];
    return copy.map((x, i) => ({ ...x, order: i }));
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title,
        section_id: state.sectionId,
        subsection_id: state.subsectionId,
        duration_min: duration,
        is_free_preview: isFreePreview,
        blocks: blocks.map((b, i) => ({ ...b, order: i })),
        attachments,
        order: lesson?.order ?? 0,
      };
      if (lesson?.id) {
        await api.patch(`/courses/admin/${courseId}/lessons/${lesson.id}`, payload);
        toast.success("تم تحديث الدرس");
      } else {
        await api.post(`/courses/admin/${courseId}/lessons`, payload);
        toast.success("تم إضافة الدرس");
      }
      onSaved(); onClose();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={state.open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent dir="rtl" className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="lesson-editor">
        <DialogHeader><DialogTitle>{lesson ? "تعديل الدرس" : "درس جديد"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>عنوان الدرس (يُعرض فوق الفيديوهات)</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required data-testid="lesson-title-input" placeholder="مثلًا: اليوم الأول - المقدمة" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2"><Label>المدة (دقائق)</Label><Input type="number" min={1} value={duration} onChange={(e) => setDuration(+e.target.value)} data-testid="lesson-duration-input" /></div>
            <div className="space-y-2 col-span-2">
              <Label>معاينة مجانية</Label>
              <label className="flex items-center gap-2 h-10 text-sm">
                <input type="checkbox" checked={isFreePreview} onChange={(e) => setIsFreePreview(e.target.checked)} className="h-4 w-4" data-testid="lesson-free-preview" />
                <span className="text-foreground/70">قابل للمشاهدة قبل الشراء</span>
              </label>
            </div>
          </div>

          <div className="border-t pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm">محتوى الدرس (فيديو + صور + نصوص بأي ترتيب)</Label>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={addVideoBlock} data-testid="add-video-block" className="gap-1"><Video className="h-3 w-3" />فيديو</Button>
                <Button size="sm" variant="outline" onClick={addImageBlock} data-testid="add-image-block" className="gap-1"><ImageIcon className="h-3 w-3" />صورة</Button>
                <Button size="sm" variant="outline" onClick={addTextBlock} data-testid="add-text-block" className="gap-1"><Type className="h-3 w-3" />نص</Button>
              </div>
            </div>

            {blocks.length === 0 ? (
              <div className="text-sm text-foreground/50 py-8 text-center border border-dashed border-foreground/15 rounded-lg">
                أضف كتلة فيديو، صورة أو نص لبدء بناء الدرس. يمكنك مزج أي عدد بأي ترتيب.
              </div>
            ) : (
              <div className="space-y-3">
                {blocks.map((b, i) => (
                  <BlockCard key={b.id} block={b} idx={i} total={blocks.length}
                             onChange={(patch) => updateBlock(b.id, patch)}
                             onRemove={() => removeBlock(b.id)}
                             onMoveUp={() => moveBlock(b.id, -1)}
                             onMoveDown={() => moveBlock(b.id, 1)} />
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2 border-t pt-4">
            <Label className="flex items-center gap-2"><Paperclip className="h-4 w-4" /> ملفات مرفقة (اختياري)</Label>
            {attachments.length > 0 && (
              <div className="space-y-1">
                {attachments.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 bg-foreground/5 rounded-lg px-3 py-2 text-sm">
                    <Paperclip className="h-3 w-3 shrink-0" />
                    <a href={backendUrl(a.url)} target="_blank" rel="noreferrer" className="flex-1 truncate underline">{a.name}</a>
                    <button type="button" onClick={() => setAttachments(attachments.filter((_, k) => k !== i))} className="text-destructive"><X className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            )}
            <FileUpload kind="document" onChange={(f) => f && setAttachments([...attachments, { name: f.original_filename || "ملف", url: f.url, kind: f.content_type }])} testid="lesson-attachment-upload" label={null} />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>إلغاء</Button>
          <Button type="button" onClick={save} disabled={saving || !title.trim()} className="bg-black hover:bg-black/80 text-white" data-testid="lesson-save">
            {saving ? "جاري الحفظ..." : (lesson ? "حفظ التعديلات" : "إضافة الدرس")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===========================================================================
// Individual content block card
// ===========================================================================
function BlockCard({ block, idx, total, onChange, onRemove, onMoveUp, onMoveDown }) {
  const isVideo = block.type === "video";
  const isImage = block.type === "image";
  const badge = isVideo
    ? (<><Video className="h-3 w-3" /> كتلة فيديو</>)
    : isImage
    ? (<><ImageIcon className="h-3 w-3" /> كتلة صورة</>)
    : (<><Type className="h-3 w-3" /> كتلة نص</>);
  return (
    <Card className="p-3 space-y-2 bg-white border-foreground/10" data-testid={`block-card-${block.id}`}>
      <div className="flex items-center gap-2">
        <span className="h-6 w-6 rounded-full bg-orange-100 text-orange-700 text-xs grid place-items-center font-medium">{idx + 1}</span>
        <div className="flex-1 text-xs font-medium text-foreground/60 flex items-center gap-1">
          {badge}
        </div>
        <Button size="icon" variant="ghost" className="h-6 w-6" disabled={idx === 0} onClick={onMoveUp} data-testid={`block-up-${block.id}`}><ArrowUp className="h-3 w-3" /></Button>
        <Button size="icon" variant="ghost" className="h-6 w-6" disabled={idx === total - 1} onClick={onMoveDown} data-testid={`block-down-${block.id}`}><ArrowDown className="h-3 w-3" /></Button>
        <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={onRemove} data-testid={`block-remove-${block.id}`}><Trash2 className="h-3 w-3" /></Button>
      </div>

      {isVideo ? (
        <div className="space-y-2">
          <Input placeholder="عنوان الفيديو (يُعرض فوقه، اختياري)" value={block.video_title || ""}
                 onChange={(e) => onChange({ video_title: e.target.value })} data-testid={`block-video-title-${block.id}`} />

          <div className="grid grid-cols-3 gap-2 items-start">
            <Select value={block.video_source || "vimeo"} onValueChange={(v) => onChange({ video_source: v })}>
              <SelectTrigger data-testid={`block-video-source-${block.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cf_stream">Cloudflare Stream (موصى به)</SelectItem>
                <SelectItem value="vimeo">Vimeo (لصق الرابط)</SelectItem>
                <SelectItem value="upload">رفع مباشر (Emergent)</SelectItem>
              </SelectContent>
            </Select>
            <div className="col-span-2">
              {block.video_source === "cf_stream" ? (
                <CloudflareVideoUpload
                  value={block.stream_uid}
                  onChange={({ uid, iframe_url }) => onChange({ stream_uid: uid, stream_iframe_url: iframe_url })}
                  testid={`block-cf-stream-${block.id}`}
                />
              ) : block.video_source === "upload" ? (
                <FileUpload kind="video" value={block.upload_url}
                            onChange={(v) => onChange({ upload_url: fileToUrl(v) })}
                            testid={`block-video-upload-${block.id}`} />
              ) : (
                <Input placeholder="رابط أو ID فيديو Vimeo (مثلاً 76979871 أو https://vimeo.com/76979871)"
                       value={block.vimeo_id || ""}
                       onChange={(e) => onChange({ vimeo_id: extractVimeoId(e.target.value) || e.target.value })}
                       dir="ltr" data-testid={`block-vimeo-input-${block.id}`} />
              )}
            </div>
          </div>

          {/* Live preview */}
          {(block.video_source === "vimeo" && extractVimeoId(block.vimeo_id)) && (
            <div className="aspect-video rounded-lg overflow-hidden bg-black">
              <iframe src={vimeoEmbedUrl(block.vimeo_id)} className="w-full h-full" allow="autoplay; fullscreen" allowFullScreen title="preview" />
            </div>
          )}
          {(block.video_source === "upload" && block.upload_url) && (
            <video src={backendUrl(block.upload_url)} controls className="w-full aspect-video rounded-lg bg-black" />
          )}
          {(block.video_source === "cf_stream" && block.stream_iframe_url) && (
            <div className="aspect-video rounded-lg overflow-hidden bg-black">
              <iframe src={block.stream_iframe_url} className="w-full h-full" allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;" allowFullScreen title="preview" />
            </div>
          )}

          <Textarea placeholder="نص أسفل الفيديو (شرح، تعليمات، اختياري)" value={block.caption || ""}
                    onChange={(e) => onChange({ caption: e.target.value })} className="min-h-[80px]"
                    data-testid={`block-caption-${block.id}`} />
        </div>
      ) : isImage ? (
        <div className="space-y-2">
          <FileUpload kind="image" value={block.image_url}
                      onChange={(v) => onChange({ image_url: fileToUrl(v) })}
                      testid={`block-image-upload-${block.id}`} />
          <Input placeholder="نص بديل للصورة (لإمكانية الوصول، اختياري)" value={block.image_alt || ""}
                 onChange={(e) => onChange({ image_alt: e.target.value })}
                 data-testid={`block-image-alt-${block.id}`} />
          <Textarea placeholder="تعليق أسفل الصورة (اختياري)" value={block.caption || ""}
                    onChange={(e) => onChange({ caption: e.target.value })} className="min-h-[60px]"
                    data-testid={`block-image-caption-${block.id}`} />
        </div>
      ) : (
        <Textarea placeholder="اكتب النص هنا... (يدعم HTML بسيط للتنسيق)" value={block.content || ""}
                  onChange={(e) => onChange({ content: e.target.value })} className="min-h-[140px]"
                  data-testid={`block-text-content-${block.id}`} />
      )}
    </Card>
  );
}
